import { AccentThemeProvider } from '@vimeo/accent-theme';
import { DarkMode, LightMode, VStack } from '@vimeo/bokeh';
import { LayoutProvider } from '@vimeo/clip-ui/hooks/use-layout';
import { ENTITY_TYPE } from '@vimeo/lead-capture-widgets/exports/constants';
import { useLeadFormPrefillParentListener } from '@vimeo/lead-capture-widgets/exports/hooks';
import { getSearchParams } from '@vimeo/viewer-showcase-ui/utils/helper';

import { useGetShowcase } from '../hooks/use-get-showcase';
import { FooterModule, NavigationModule } from '../modules';
import { LoadingPage } from '../pages/LoadingPage';
import { ShowcasePage } from '../pages/ShowcasePage';
import { useAlbumStore } from '../state/albumStore';
import { DEFAULT_BRAND_COLOR_DARK_THEME } from '../utils/constants';

/**
 * ViewerShowcases component is the new redesign for the showcases page. This will be the new landing page for showcases.
 * https://vimean.atlassian.net/browse/VH-820
 */
export function ViewerShowcases(): React.ReactElement {
  const albumId = useAlbumStore((state) => state.albumId);
  const setBannerHeight = useAlbumStore((state) => state.setBannerHeight);
  const searchParams = getSearchParams();
  const bannerHeight = searchParams.get('height');
  if (bannerHeight) {
    setBannerHeight(bannerHeight);
  }

  const {
    isLoading: isShowcaseDataLoading,
    hideNav,
    brandColor,
    colorMode,
    thumbnailUrl,
  } = useGetShowcase();
  useLeadFormPrefillParentListener(String(albumId), ENTITY_TYPE.SHOWCASE);

  if (isShowcaseDataLoading) {
    return <LoadingPage />;
  }

  const ColorModeWrapper = colorMode === 'dark' ? DarkMode : LightMode;

  return (
    <ColorModeWrapper>
      <VStack spacing={0} align="stretch" flex="1" backgroundColor="background">
        {!hideNav ? <NavigationModule /> : null}

        <AccentThemeProvider
          accentColor={thumbnailUrl && !brandColor ? DEFAULT_BRAND_COLOR_DARK_THEME : brandColor}
        >
          {/* Every layer of theme needs a layer of color mode */}
          <ColorModeWrapper>
            <LayoutProvider isShowcaseLayout>
              <ShowcasePage />
            </LayoutProvider>
          </ColorModeWrapper>
        </AccentThemeProvider>

        {!hideNav ? <FooterModule /> : null}
      </VStack>
    </ColorModeWrapper>
  );
}
